// Re:Focus — Chat Client Module
// AI chat panel, message streaming, chat settings/config

import { SESSION_KEY } from './auth-client.js';

let _trapFocus = () => () => {};
let _AI_NAME = 'Cap';

export function initChatClient({ trapFocus, getAiName }) {
  _trapFocus = trapFocus;
  if (getAiName) _AI_NAME = getAiName();
}

let _chatHistory = [];

function chatHistoryKey() {
  const userId = localStorage.getItem('refocus_user_id') || 'default';
  return `refocus_chat_history_${userId}`;
}
function saveChatHistory() {
  try { localStorage.setItem(chatHistoryKey(), JSON.stringify(_chatHistory.slice(-100))); } catch {}
}
function loadChatHistory() {
  try {
    const raw = localStorage.getItem(chatHistoryKey());
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
let _chatOpen = false;

/** Called when the unified panel opens — load chat history & set title */
export function openAiChat() {
  _chatOpen = true;
  document.getElementById('ai-chat-title').textContent = _AI_NAME;
  if (_chatHistory.length === 0) {
    _chatHistory = loadChatHistory();
    const msgs = document.getElementById('ai-chat-messages');
    if (msgs && _chatHistory.length > 0) {
      msgs.innerHTML = '';
      for (const msg of _chatHistory) appendChatBubble(msg.role, msg.content);
    }
  }
}

/** Called when the unified panel closes */
export function closeAiChat() {
  _chatOpen = false;
}

function renderBubbleContent(el, content) {
  el.innerHTML = content
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
}

function appendChatBubble(role, content) {
  const msgs = document.getElementById('ai-chat-messages');
  if (!msgs) return null;
  const wrap = document.createElement('div');
  wrap.className = `ai-chat-msg ai-chat-msg-${role}`;
  const bubble = document.createElement('div');
  bubble.className = 'ai-chat-bubble';
  renderBubbleContent(bubble, content);
  wrap.appendChild(bubble);
  msgs.appendChild(wrap);
  msgs.scrollTop = msgs.scrollHeight;
  return bubble;
}

function showChatTyping() {
  const msgs = document.getElementById('ai-chat-messages');
  if (!msgs || document.getElementById('ai-chat-typing')) return;
  const wrap = document.createElement('div');
  wrap.className = 'ai-chat-msg ai-chat-msg-assistant';
  wrap.id = 'ai-chat-typing';
  wrap.innerHTML = '<div class="ai-chat-bubble ai-chat-typing-bubble"><span></span><span></span><span></span></div>';
  msgs.appendChild(wrap);
  msgs.scrollTop = msgs.scrollHeight;
}

function hideChatTyping() {
  document.getElementById('ai-chat-typing')?.remove();
}

async function sendChatMessage() {
  const input = document.getElementById('ai-chat-input');
  const sendBtn = document.getElementById('ai-chat-send');
  const text = input?.value.trim();
  if (!text) return;

  input.value = '';
  input.style.height = '';
  input.disabled = true;
  sendBtn.disabled = true;

  _chatHistory.push({ role: 'user', content: text });
  appendChatBubble('user', text);
  showChatTyping();

  try {
    const token = localStorage.getItem(SESSION_KEY);
    if (!token) throw new Error('Not authenticated');

    const resp = await fetch('/api/v1/chat', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: _chatHistory, stream: true }),
    });

    if (!resp.ok) {
      hideChatTyping();
      let errMsg = 'Something went wrong.';
      try { const d = await resp.json(); errMsg = d.error || errMsg; } catch {}
      appendChatBubble('assistant', `⚠ ${errMsg}`);
      return;
    }

    hideChatTyping();
    const bubble = appendChatBubble('assistant', '');
    const contentEl = bubble.querySelector('.chat-bubble-text') || bubble;

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let fullReply = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (raw === '[DONE]') break;
        try {
          const chunk = JSON.parse(raw);
          const delta = chunk.choices?.[0]?.delta?.content || '';
          if (delta) {
            fullReply += delta;
            contentEl.textContent = fullReply;
            const msgs = document.getElementById('ai-chat-messages');
            if (msgs) msgs.scrollTop = msgs.scrollHeight;
          }
        } catch {}
      }
    }

    if (!fullReply) {
      contentEl.textContent = '⚠ AI returned an empty response.';
    } else {
      renderBubbleContent(contentEl, fullReply);
      _chatHistory.push({ role: 'assistant', content: fullReply });
      saveChatHistory();
    }
  } catch (err) {
    hideChatTyping();
    appendChatBubble('assistant', `⚠ Couldn't reach the server. Check your connection.`);
  } finally {
    input.disabled = false;
    sendBtn.disabled = false;
    input.focus();
  }
}

export function initAiChatPanel() {
  const chatSend = document.getElementById('ai-chat-send');
  const chatInput = document.getElementById('ai-chat-input');

  document.getElementById('ai-chat-clear')?.addEventListener('click', () => {
    if (!confirm('Clear chat history?')) return;
    _chatHistory = [];
    try { localStorage.removeItem(chatHistoryKey()); } catch {}
    const msgs = document.getElementById('ai-chat-messages');
    if (msgs) msgs.innerHTML = '';
  });

  chatSend?.addEventListener('click', sendChatMessage);

  chatInput?.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      sendChatMessage();
    }
  });

  chatInput?.addEventListener('input', () => {
    chatInput.style.height = 'auto';
    chatInput.style.height = Math.min(chatInput.scrollHeight, 120) + 'px';
  });

  // ── AI Chat settings ──
  const chatSaveBtn = document.getElementById('chat-settings-save-btn');
  const chatEndpointInput = document.getElementById('chat-endpoint-input');
  const chatModelInput = document.getElementById('chat-model-input');
  const chatApikeyInput = document.getElementById('chat-apikey-input');
  const chatKeyStatus = document.getElementById('chat-key-status');

  (async () => {
    const token = localStorage.getItem(SESSION_KEY);
    if (!token) return;
    try {
      const res = await fetch('/api/v1/chat', { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) return;
      const cfg = await res.json();
      if (chatEndpointInput && !chatEndpointInput.value) chatEndpointInput.value = cfg.endpoint || '';
      if (chatModelInput && !chatModelInput.value) chatModelInput.value = cfg.model || '';
      if (chatApikeyInput) chatApikeyInput.placeholder = cfg.hasKey
        ? '••••••• (key saved — paste to replace)'
        : 'Paste your API key';
      if (chatKeyStatus) {
        chatKeyStatus.textContent = cfg.hasKey ? '✓ Key saved' : '';
        chatKeyStatus.classList.toggle('hidden', !cfg.hasKey);
      }
    } catch { /* ignore */ }
  })();

  const chatPill = document.getElementById('chat-validation-pill');
  const chatDebugOut = document.getElementById('chat-debug-out');

  function setChatPill(state, msg) {
    if (!chatPill) return;
    chatPill.className = `chat-validation-pill chat-validation-pill-${state}`;
    chatPill.textContent = msg;
    chatPill.classList.remove('hidden');
  }

  function chatDebug(lines) {
    if (!chatDebugOut) return;
    chatDebugOut.textContent = lines.join('\n');
    chatDebugOut.classList.remove('hidden');
  }

  document.getElementById('chat-prefill-anthropic')?.addEventListener('click', () => {
    if (chatEndpointInput) chatEndpointInput.value = 'https://openrouter.ai/api/v1';
    if (chatModelInput) chatModelInput.value = 'anthropic/claude-sonnet-4-5';
    chatApikeyInput?.focus();
  });

  document.getElementById('chat-prefill-openclaw')?.addEventListener('click', async () => {
    const token = localStorage.getItem(SESSION_KEY);
    if (chatModelInput) chatModelInput.value = 'openclaw:main';
    chatApikeyInput?.focus();
    try {
      const res = await fetch('/api/v1/chat/tunnel', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (chatEndpointInput && data.url) chatEndpointInput.value = data.url;
      }
    } catch { /* ignore — user can paste manually */ }
  });

  chatSaveBtn?.addEventListener('click', async () => {
    const token = localStorage.getItem(SESSION_KEY);
    const endpoint = chatEndpointInput?.value.trim() || '';
    const model = chatModelInput?.value.trim() || '';
    const apiKey = chatApikeyInput?.value.trim() || '';

    const missing = [];
    if (!endpoint) missing.push('Endpoint URL');
    if (!apiKey) missing.push('API Key');
    if (missing.length) {
      setChatPill('error', `✗ Missing: ${missing.join(', ')}`);
      chatDebug([`Missing required fields: ${missing.join(', ')}`]);
      return;
    }
    if (!token) {
      setChatPill('error', '✗ Not logged in');
      return;
    }

    chatSaveBtn.disabled = true;
    chatSaveBtn.textContent = 'Saving…';
    if (chatPill) chatPill.classList.add('hidden');
    chatDebugOut?.classList.add('hidden');

    const debugLines = [
      `Endpoint: ${endpoint}`,
      `Model: ${model || '(default)'}`,
      `Key: ${apiKey.slice(0, 4)}…${apiKey.slice(-4)} (${apiKey.length} chars)`,
      `Token: ${token ? 'present' : 'MISSING'}`,
    ];

    try {
      debugLines.push('→ Saving config…');
      chatDebug(debugLines);

      const saveRes = await fetch('/api/v1/chat', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint, model, apiKey }),
      });
      const saveData = await saveRes.json();
      debugLines.push(`← Save: ${saveRes.status} ${JSON.stringify(saveData)}`);
      chatDebug(debugLines);

      if (!saveRes.ok) {
        setChatPill('error', `✗ Save failed: ${saveData.error || saveRes.status}`);
        chatSaveBtn.disabled = false; chatSaveBtn.textContent = 'Save Connection';
        return;
      }

      if (chatApikeyInput) { chatApikeyInput.value = ''; chatApikeyInput.placeholder = '••••••• (key saved — paste to replace)'; }
      if (chatKeyStatus) { chatKeyStatus.textContent = '✓ Key saved'; chatKeyStatus.classList.remove('hidden'); }
      chatSaveBtn.textContent = 'Saved!';
      setTimeout(() => { chatSaveBtn.textContent = 'Save Connection'; chatSaveBtn.disabled = false; }, 1500);

      setChatPill('testing', '⟳ Testing…');
      debugLines.push('→ Testing connection…');
      chatDebug(debugLines);

      const testRes = await fetch('/api/v1/chat', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [{ role: 'user', content: 'ok' }], test: true, endpoint, model, apiKey }),
      });
      const testData = await testRes.json();
      debugLines.push(`← Test: ${testRes.status} ${JSON.stringify(testData).slice(0, 120)}`);
      chatDebug(debugLines);

      if (!testRes.ok) {
        setChatPill('error', `✗ ${testData.error || 'Connection failed'}`);
      } else {
        setChatPill('ok', '✓ Validated');
        debugLines.push('✓ Connection validated successfully');
        chatDebug(debugLines);
      }
    } catch (err) {
      debugLines.push(`✗ Exception: ${err.message}`);
      chatDebug(debugLines);
      setChatPill('error', '✗ Network error — see debug below');
      chatSaveBtn.textContent = 'Save Connection';
      chatSaveBtn.disabled = false;
    }
  });
}
